import React, { useEffect, useRef, useState } from 'react';
import classnames from 'classnames';
import Select from 'react-select';
import useApi from 'hooks/useApi';
import useDebounce from 'hooks/useDebounce';
import { MultiSelectProps, Option } from 'types/dynamicInputs';

const MultiSelect = ({
  label,
  endpoint,
  value,
  staticOptions,
  onChange,
  onBlur,
  disabled,
  pristine,
  error,
  limit = 100,
  endpointParams = {},
  avoidFirstLoad: _avoidFirstLoad = false,
  formatOptionLabel
}: MultiSelectProps) => {
  const [query, setQuery] = useState('');
  const [avoidFirstLoad, setAvoidFirstLoad] = useState(_avoidFirstLoad);
  const qDebounced = useDebounce(query);
  const [options, setOptions] = useState<Option[]>([]);
  const ref = useRef(null);
  const api = useApi();

  const isStatic = !!staticOptions && staticOptions.length > 0;

  useEffect(() => {
    if (endpoint && !avoidFirstLoad) {
      api.fetchData(endpoint.get(endpointParams), { q: qDebounced, limit });
    }
  }, [qDebounced, endpoint]);

  // Trigger initial fetch once when avoidFirstLoad flips false
  useEffect(() => {
    if (endpoint && !avoidFirstLoad) {
      api.fetchData(endpoint.get(endpointParams), { q: '', limit });
    }
  }, [endpoint, avoidFirstLoad]);

  useEffect(() => {
    if (!isStatic && api.response) {
      setOptions(api.response.items);
    } else if (isStatic) {
      setOptions(staticOptions);
    }
  }, [api.response, isStatic]);

  useEffect(() => {
    setAvoidFirstLoad(false);
  }, []);

  // Combined formatOptionLabel that handles error display
  const combinedFormatOptionLabel = (option: Option, formatLabelContext?: any) => {
    // Use custom formatOptionLabel if provided, passing both parameters for compatibility
    if (formatOptionLabel) {
      return formatOptionLabel(option, formatLabelContext);
    }

    // Default format: show label and errors if exists
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
        <span>{option.label}</span>
        {option.errors && option.errors.length > 0 && (
          <span style={{ color: '#dc3545', fontSize: '12px' }}>{option.errors.join(', ')}</span>
        )}
      </div>
    );
  };

  return (
    <div className="input-wrapper" ref={ref}>
      {!!label && <label className="label">{label}</label>}
      <Select
        className={classnames('multi-select', { error: !pristine && error })}
        classNamePrefix="multi"
        isDisabled={disabled}
        isLoading={api.isLoading}
        isSearchable
        isClearable={false}
        onBlur={onBlur}
        isOptionDisabled={(option: Option) => {
          // Disable if limit reached or if option has errors
          const limitReached = limit ? value.length >= limit : false;
          return limitReached || !!(option.errors && option.errors.length > 0);
        }}
        onInputChange={value => setQuery(value)}
        onChange={value => onChange(value)}
        value={value}
        options={options}
        placeholder={null}
        menuPosition="fixed"
        menuPortalTarget={ref.current}
        isMulti
        formatOptionLabel={combinedFormatOptionLabel}
      />
      {!pristine && !!error && <span className="error-message body-2">{error}</span>}
    </div>
  );
};

export default MultiSelect;
